import {
  Autocomplete,
  Button,
  Input,
  NumberInput,
  Select,
  Textarea,
} from "@mantine/core";
import { DatePicker, Upload } from "antd";
import {
  IconAlertTriangle,
  IconBuildingStore,
  IconCalendar,
  IconCash,
  IconClipboardCheck,
  IconCreditCard,
  IconFileText,
  IconHash,
  IconMapPin,
  IconNotes,
  IconPackage,
  IconPaperclip,
  IconReceipt,
  IconShoppingCart,
  IconTruck,
  IconUser,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { apiUserUpload } from "@/api";
import { notification } from "antd";
import { orpcClient } from "@/lib/orpc-client";
import { single, allOrThrow } from "@/lib/orpc-batch";
import { toUnitPrice } from "@/lib/purchaseOrder";
import { useData } from "@/utility/state/contextAPI";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function AddPurchaseOrder() {
  const { t } = useTranslation();
  const [currentStage, setCurrentStage] = useState(0);
  const [purchaseOrderData, setPurchaseOrderData] = useState({
    name: "",
    partnerId: "",
    purchaseDate: "",
    deliveryDate: "",
    requiresInvoice: false,
    requiresIncomingQualityControl: false,
    shippingFeeCalculation: "freeShipping",
    deliveryMethod: "single",
    deliveryLocation: "",
    remark: "",
    notice: "",
  });
  const [tempProductList, setTempProductList] = useState<any[]>([]);
  const [uploadState, setUploadState] = useState(false);
  const [fileUpload, setFileUpload] = useState([]);
  const [tempProductInput, setTempProductInput] = useState([]);
  const [tempProductData, setTempProductData] = useState([]);
  const [api, contextHolder] = notification.useNotification();
  const [tempAmount, setTempAmount] = useState([]);
  const [tempUnitPrice, setTempUnitPrice] = useState<any[]>([]);

  const [partnerData, setPartnerData] = useState([]);
  const {
    modal: { contextOpenModal, contextCloseModal, contextSetModalContent },
  } = useData();
  const navigate = useNavigate();
  const {
    shippingFeeCalculation,
    deliveryMethod,
    requiresInvoice,
    requiresIncomingQualityControl,
    deliveryLocation,
    remark,
    notice,
  } = purchaseOrderData || {};

  const handleDataChange = (name, value) => {
    setPurchaseOrderData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleAddProductList = () => {
    setTempProductList((prev) => [...prev, []]);
  };

  const handleChangePartnerData = (value, data, dataType) => {
    let match = value.match(/ID:\s*([a-f0-9-]+)/);
    let idNumber = match ? match[1] : null;

    let selectedData = data.filter((e) => e?.id === idNumber)[0];

    handleDataChange(dataType, selectedData?.id);
  };

  // deliveryLocation 後端已改為純文字欄位，原本的縣市／行政區／郵遞區號／地址拆欄先保留註解
  // const handleAddressChange = (type, value) => {
  //   setPurchaseOrderData((prevData) => ({
  //     ...prevData,
  //     deliveryLocation: {
  //       ...prevData?.deliveryLocation,
  //       [type]: value,
  //     },
  //   }));
  // };

  const handleChangeImg = async (info) => {
    setUploadState(true);
    if (!uploadState) {
      const file = info.file.originFileObj;

      const formData = new FormData();
      formData.append("files", file, file.name);
      formData.append("name", info.file.name);

      const fileInfo = {
        alternativeText: localStorage.getItem("companyId"),
      };
      formData.append("fileInfo", JSON.stringify(fileInfo));

      let result = await apiUserUpload(formData);

      if (result[0]?.id) {
        setFileUpload((prev) => [...prev, result[0]]);
      } else {
        console.error(`apiUserUpload went wrong ${result}`);
      }
      setUploadState(false);
    }
  };

  const handleSubmit = async ({ data, tempProductList }) => {
    if (
      data.purchaseDate &&
      data.deliveryDate &&
      new Date(data.purchaseDate) > new Date(data.deliveryDate)
    ) {
      api.error({
        message: `Notification`,
        description: <div>{t("purchaseOrder.purchaseDateError")}</div>,
        placement: "topLeft",
        duration: 0,
      });
      return;
    }
    if (tempProductList && tempProductList?.length) {
      const invalidProduct = tempProductList.some(
        (t) => !t?.amount || !t?.product?.id
      );

      if (invalidProduct) {
        api.error({
          message: `Notification`,
          description: <div>{t("purchaseOrder.productListItemsRequired")}</div>,
          placement: "topLeft",
          duration: 0,
        });
        return;
      }
    }
    try {
      const submitData = {
        ...data,
        purchaseDate: data.purchaseDate
          ? new Date(data.purchaseDate)
          : undefined,
        deliveryDate: data.deliveryDate
          ? new Date(data.deliveryDate)
          : undefined,
        orderReminderDate: data.orderReminderDate
          ? new Date(data.orderReminderDate)
          : undefined,
      };

      const result = await single(orpcClient.purchaseOrder.create([submitData]));

      if (result) {
        if (tempProductList && tempProductList?.length) {
          await allOrThrow(
            orpcClient.purchaseOrderProduct.create(
              tempProductList.map((productData) => ({
                productId: productData?.product?.id,
                amount: Number(productData?.amount),
                unitPrice: toUnitPrice(productData?.unitPrice),
                purchaseOrderId: result.id,
              }))
            )
          );
        }
        setCurrentStage(1);
      }
    } catch (error) {
      console.error("Error post purchaseOrder data:", error);
      api.error({
        message: `Notification`,
        description: (
          <div>{error?.message || t("purchaseOrder.createFailed")}</div>
        ),
        placement: "topLeft",
        duration: 0,
      });
    }
  };

  const handleOnOptionSubmit = (type, e, data, index) => {
    let selectedData;
    if (type === "product") {
      selectedData = data.filter((item) => item?.name === e)[0];
      setTempProductList((prev) => {
        const updatedList = [...prev];
        updatedList[index] = { ...updatedList[index], [type]: selectedData };
        return updatedList;
      });
    } else {
      let idBehindMatch = e.match(/ID:\s*([a-f0-9-]+)/);
      let documentID = idBehindMatch ? idBehindMatch[1].trim() : null;

      setTempProductList((prev) => {
        const updatedList = [...prev];
        updatedList[index] = {
          ...updatedList[index],
          storageLocation: documentID,
        };
        return updatedList;
      });
    }
  };

  const handleDisplaySelectMembers = (data) => {
    return data?.map((t) => ({ value: t?.id, label: t?.name }));
  };

  const handleDeleteProductList = (index) => {
    const updatedProductList = [...tempProductList];

    updatedProductList.splice(index, 1);

    setTempProductInput((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempAmount((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempUnitPrice((prevInputs) => {
      const updatedInputs = [...prevInputs];
      updatedInputs.splice(index, 1);
      return updatedInputs;
    });

    setTempProductList(updatedProductList);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await orpcClient.product.list({});
        setTempProductData(result);
      } catch (error) {
        console.error("Error fetching apiUserGetProducts data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await orpcClient.partner.list({});
        setPartnerData(result);
      } catch (error) {
        console.error("Error fetching coop data:", error);
      }
    };

    fetchData();
  }, []);

  const renderControl = (label, control, Icon, required = false) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
        {required ? <span className="text-red-500">＊</span> : null}
      </label>
      {control}
    </div>
  );

  const renderSection = (title, Icon, children) => (
    <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
        <Icon size={16} className="text-blue-600" />
        <h3 className="text-sm font-medium text-gray-700">{title}</h3>
      </div>
      {children}
    </div>
  );

  return currentStage === 0 ? (
    <div className="min-h-screen p-8">
      {contextHolder}
      <div className="mx-auto max-w-4xl rounded-xl bg-white p-8 space-y-5">
        <div>
          <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("purchaseOrder.createPurchaseOrder")}
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {t("purchaseOrder.add") || "新增採購單"}
          </div>
        </div>

        {renderSection(
          t("purchaseOrder.basicInfo"),
          IconShoppingCart,
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {renderControl(
              t("purchaseOrder.purchaseOrderName") || "採購單名稱",
              <Input
                leftSection={<IconShoppingCart size={16} />}
                onChange={(e) => handleDataChange("name", e.target.value)}
              />,
              IconShoppingCart
            )}
            {renderControl(
              t("purchaseOrder.purchaseDate") || "採購日期",
              <DatePicker
                style={{ width: "100%" }}
                onChange={(e) =>
                  handleDataChange("purchaseDate", e.format("YYYY-MM-DD"))
                }
              />,
              IconCalendar,
              true
            )}
            {renderControl(
              t("purchaseOrder.partner") || "合作方",
              <Autocomplete
                leftSection={<IconBuildingStore size={16} />}
                data={partnerData?.map((t) => t?.name + ` (ID: ${t?.id})`)}
                onChange={(e) =>
                  handleChangePartnerData(e, partnerData, "partnerId")
                }
              />,
              IconBuildingStore,
              true
            )}
            {renderControl(
              t("purchaseOrder.paymentMethod"),
              <Input
                leftSection={<IconCreditCard size={16} />}
                onChange={(e) =>
                  handleDataChange("paymentMethod", e.target.value)
                }
              />,
              IconCreditCard
            )}
            {renderControl(
              t("purchaseOrder.shippingFeeCalculation"),
              <Select
                value={
                  shippingFeeCalculation === "freeShipping"
                    ? t("purchaseOrder.freeShipping")
                    : t("purchaseOrder.shippingFeeRequired")
                }
                data={[
                  t("purchaseOrder.freeShipping"),
                  t("purchaseOrder.shippingFeeRequired"),
                ]}
                onChange={(value) =>
                  handleDataChange(
                    "shippingFeeCalculation",
                    value === t("purchaseOrder.shippingFeeRequired")
                      ? "shippingFeeRequired"
                      : "freeShipping"
                  )
                }
                leftSection={<IconTruck size={16} />}
              />,
              IconTruck
            )}
            {renderControl(
              t("purchaseOrder.actualShippingFee"),
              <NumberInput
                leftSection={<IconCash size={16} />}
                onChange={(e) => handleDataChange("actualShippingFee", e)}
              />,
              IconCash
            )}
            {renderControl(
              t("purchaseOrder.orderReminderDate"),
              <DatePicker
                style={{ width: "100%" }}
                onChange={(e) =>
                  handleDataChange("orderReminderDate", e.format("YYYY-MM-DD"))
                }
              />,
              IconCalendar
            )}
            {renderControl(
              t("purchaseOrder.requiresInvoice"),
              <Select
                value={
                  requiresInvoice
                    ? t("purchaseOrder.yes")
                    : t("purchaseOrder.no")
                }
                data={[t("purchaseOrder.yes"), t("purchaseOrder.no")]}
                onChange={(value) =>
                  handleDataChange(
                    "requiresInvoice",
                    value === t("purchaseOrder.yes") ? true : false
                  )
                }
                leftSection={<IconReceipt size={16} />}
              />,
              IconReceipt
            )}
            {renderControl(
              t("purchaseOrder.requiresIncomingQualityControl"),
              <Select
                value={
                  requiresIncomingQualityControl
                    ? t("purchaseOrder.yes")
                    : t("purchaseOrder.no")
                }
                data={[t("purchaseOrder.yes"), t("purchaseOrder.no")]}
                onChange={(value) =>
                  handleDataChange(
                    "requiresIncomingQualityControl",
                    value === t("purchaseOrder.yes") ? true : false
                  )
                }
                leftSection={<IconClipboardCheck size={16} />}
              />,
              IconClipboardCheck
            )}
          </div>
        )}

        {renderSection(
          t("purchaseOrder.incomingQCItems"),
          IconPaperclip,
          <div className="space-y-3 p-6">
            <Upload
              name="avatar"
              showUploadList={false}
              onChange={handleChangeImg}
            >
              <Button variant="default" radius="md" size="sm">
                {t("purchaseOrder.addFile")}
              </Button>
            </Upload>
            {fileUpload.length ? (
              <div className="overflow-hidden rounded-lg border border-gray-200">
                {fileUpload.map((uploadedFile) => (
                  <div
                    key={uploadedFile?.id}
                    className="flex items-center justify-between border-b border-gray-100 px-4 py-2 last:border-b-0"
                  >
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <IconFileText size={15} className="text-gray-400" />
                      {uploadedFile?.name}
                    </div>
                    <button
                      type="button"
                      className="text-red-500 hover:text-red-700"
                      onClick={() =>
                        setFileUpload((prev) =>
                          prev.filter((file) => file.id !== uploadedFile?.id)
                        )
                      }
                    >
                      ❌
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconPackage size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("purchaseOrder.product")}
            </h3>
          </div>
          <div className="space-y-4 p-6">
            {tempProductList.length ? (
              tempProductList.map((_item, i) => (
                <div
                  key={i}
                  className="grid grid-cols-1 items-end gap-4 rounded-lg border border-gray-200 p-4 md:grid-cols-[2fr_1fr_1fr_auto]"
                >
                  {renderControl(
                    t("purchaseOrder.productName"),
                    <Select
                      searchable
                      nothingFoundMessage="查無資料"
                      leftSection={<IconPackage size={16} />}
                      data={handleDisplaySelectMembers(tempProductData)}
                      value={tempProductInput[i] || null}
                      onChange={(value) => {
                        setTempProductInput((prev) => {
                          const updated = [...prev];
                          updated[i] = value;
                          return updated;
                        });
                        const selectedData = tempProductData.find(
                          (p) => p?.id === value
                        );
                        setTempProductList((prev) => {
                          const updated = [...prev];
                          updated[i] = {
                            ...updated[i],
                            product: selectedData,
                          };
                          return updated;
                        });
                      }}
                    />,
                    IconPackage
                  )}
                  {renderControl(
                    t("purchaseOrder.quantity"),
                    <NumberInput
                      leftSection={<IconHash size={16} />}
                      value={tempAmount[i] || ""}
                      onChange={(value) => {
                        setTempProductList((prev) => {
                          const updatedList = [...prev];
                          updatedList[i] = {
                            ...updatedList[i],
                            amount: value,
                          };
                          return updatedList;
                        });

                        setTempAmount((prev) => {
                          const updated = [...prev];
                          updated[i] = value;
                          return updated;
                        });
                      }}
                      min={0}
                    />,
                    IconHash
                  )}
                  {renderControl(
                    t("purchaseOrder.unitPrice") || "單價",
                    <NumberInput
                      leftSection={<IconCash size={16} />}
                      value={tempUnitPrice[i] ?? ""}
                      decimalScale={2}
                      onChange={(value) => {
                        setTempProductList((prev) => {
                          const updatedList = [...prev];
                          updatedList[i] = {
                            ...updatedList[i],
                            unitPrice: value,
                          };
                          return updatedList;
                        });

                        setTempUnitPrice((prev) => {
                          const updated = [...prev];
                          updated[i] = value;
                          return updated;
                        });
                      }}
                      min={0}
                    />,
                    IconCash
                  )}
                  <Button
                    variant="filled"
                    color="red"
                    radius="md"
                    onClick={() => handleDeleteProductList(i)}
                  >
                    {t("purchaseOrder.delete")}
                  </Button>
                </div>
              ))
            ) : (
              <div className="text-center text-sm text-gray-400">
                {t("purchaseOrder.addProductRow")}
              </div>
            )}
            <div className="flex justify-center pt-2">
              <Button
                variant="default"
                radius="md"
                size="sm"
                onClick={() => handleAddProductList()}
              >
                {t("purchaseOrder.addProductRow")}
              </Button>
            </div>
          </div>
        </div>

        {renderSection(
          t("purchaseOrder.deliverySettings"),
          IconTruck,
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {renderControl(
              t("purchaseOrder.deliveryMethodLabel"),
              <Select
                value={
                  deliveryMethod === "single"
                    ? t("purchaseOrder.singleDelivery")
                    : t("purchaseOrder.partialDelivery")
                }
                data={[
                  t("purchaseOrder.singleDelivery"),
                  t("purchaseOrder.partialDelivery"),
                ]}
                onChange={(value) =>
                  handleDataChange(
                    "deliveryMethod",
                    value === t("purchaseOrder.singleDelivery")
                      ? "single"
                      : "partial"
                  )
                }
                leftSection={<IconTruck size={16} />}
              />,
              IconTruck
            )}
            {renderControl(
              t("purchaseOrder.deliveryDate") || "交貨日期",
              <DatePicker
                style={{ width: "100%" }}
                onChange={(e) =>
                  handleDataChange("deliveryDate", e.format("YYYY-MM-DD"))
                }
              />,
              IconCalendar,
              true
            )}
            {renderControl(
              t("purchaseOrder.deliveryReceiver"),
              <Input
                leftSection={<IconUser size={16} />}
                onChange={(e) =>
                  handleDataChange("deliveryReceiver", e.target.value)
                }
              />,
              IconUser
            )}
          </div>
        )}

        {renderSection(
          t("purchaseOrder.deliveryLocationTitle"),
          IconMapPin,
          <div className="p-6">
            {renderControl(
              t("purchaseOrder.deliveryLocationTitle") || "交貨地點",
              <Input
                leftSection={<IconMapPin size={16} />}
                placeholder={
                  t("purchaseOrder.deliveryLocationPlaceholder") ||
                  "請輸入交貨地點"
                }
                value={deliveryLocation || ""}
                onChange={(e) =>
                  handleDataChange("deliveryLocation", e.target.value)
                }
              />,
              IconMapPin,
              true
            )}
          </div>
          // 後端 deliveryLocation 已由 FullAddress jsonb 改為 text，以下拆欄輸入先保留註解
          // <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2 lg:grid-cols-4">
          //   {renderControl(t("purchaseOrder.city"), <Input ... />, IconMapPin, true)}
          //   {renderControl(t("purchaseOrder.district"), <Input ... />, IconMapPin, true)}
          //   {renderControl(t("purchaseOrder.postalCode"), <InputNumber ... />, IconHash, true)}
          //   {renderControl(t("purchaseOrder.address"), <Input ... />, IconHome, true)}
          // </div>
        )}

        {renderSection(
          t("purchaseOrder.remarkNoticeTitle") || "備註與注意事項",
          IconNotes,
          <div className="grid grid-cols-1 gap-4 p-6">
            {renderControl(
              t("purchaseOrder.remark") || "備註",
              <Textarea
                autosize
                minRows={3}
                placeholder={
                  t("purchaseOrder.remarkPlaceholder") || "請輸入備註"
                }
                value={remark || ""}
                onChange={(e) => handleDataChange("remark", e.target.value)}
              />,
              IconNotes
            )}
            {renderControl(
              t("purchaseOrder.notice") || "注意事項",
              <Textarea
                autosize
                minRows={3}
                placeholder={
                  t("purchaseOrder.noticePlaceholder") || "請輸入注意事項"
                }
                value={notice || ""}
                onChange={(e) => handleDataChange("notice", e.target.value)}
              />,
              IconAlertTriangle
            )}
          </div>
        )}

        <div className="flex gap-4">
          <Button
            variant="default"
            style={{ flex: 1 }}
            radius="md"
            onClick={() => navigate("/hrMainPage/purchaseOrderDraft")}
          >
            {t("purchaseOrder.cancel")}
          </Button>
          <Button
            variant="filled"
            color="gray"
            style={{ flex: 1 }}
            radius="md"
            onClick={() =>
              handleSubmit({
                data: {
                  ...purchaseOrderData,
                  attachment:
                    fileUpload.length > 0
                      ? JSON.stringify(fileUpload.map((file) => file.id))
                      : undefined,
                },
                tempProductList,
              })
            }
          >
            {t("purchaseOrder.createAndSubmit")}
          </Button>
        </div>
      </div>
    </div>
  ) : (
    <div className="flex min-h-screen items-center justify-center p-8 text-center">
      <div className="w-[487px] rounded-xl bg-white px-16 py-20 shadow-sm">
        <div style={{ color: "#6F6F6F" }}>
          {t("purchaseOrder.createOrderSuccess")}
        </div>
        <Button
          variant="filled"
          color="gray"
          style={{ width: "100%", margin: "1rem 0 0" }}
          radius="md"
          onClick={() => navigate("/hrMainPage/purchaseOrderDraft")}
        >
          {t("purchaseOrder.confirm")}
        </Button>
      </div>
    </div>
  );
}
export default AddPurchaseOrder;
